/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Autocomplete,
  AutocompleteChangeReason,
  Grid,
  TextField,
} from "@mui/material";
import { FormsIds, GenericSettings, HoursTypes } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import { ExamHoursValidationSchema, VehicleModel } from "../../Utils/Models";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import { ExamHourProps } from "../../Utils/Types";
import { getVehicleLabel } from "../Vehicles/VehicleUtils";
import styles from "../../Styles/products.module.css";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import { AxiosResponse } from "axios";
import { onFormError } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import moment from "moment";
import { useParams } from "react-router-dom";

export default function ExamHoursForm(props: ExamHourProps) {
  const { instructor, instructors } = props;
  const [vehicles, setVehicles] = useState([] as VehicleModel[]);
  const ref: any = useRef();
  const { id } = useParams(); //order id
  const { control, setValue, handleSubmit, watch } = useForm({
    resolver: yupResolver(ExamHoursValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });
  const startHour = watch("startHour");

  useEffect(() => {
    selectInstructorAndGetItsVehicles();
  }, []);

  const selectInstructorAndGetItsVehicles = () => {
    setValue(`instructorId`, instructor._id);
    const instructorId = instructor._id;
    getData(
      `${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000&id=${instructorId}`
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setVehicles(res.data.results);
    });
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    createElement(model);
  };

  const transformModelToDto = (model: any) => {
    const { date, startHour, endHour } = model;

    const formattedStartHour = moment(startHour).format("HH:mm");
    const formattedEndHour = moment(endHour).format("HH:mm");
    const formattedDate = moment(date).format("YYYY-MM-DD");

    model.startDate = `${formattedDate}T${formattedStartHour}:00`;
    model.endDate = `${formattedDate}T${formattedEndHour}:00`;
    model.orderId = id;
    model.type = HoursTypes.exam;

    return model;
  };
  /**
   * Create a new class and add it to the database.
   * @param {object} model The new class data.
   * @returns {Promise<void>}
   */
  const createElement = async (model: any) => {
    const url = `${endpoints.hours.main}`;

    const dto = transformModelToDto(model);

    try {
      const res = (await postData(url, dto)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
    } catch (error) {
      console.error("Error creating class:", error);
    }
  };

  const getValueForAutocompleteInstructors = () => {
    const value = instructor;

    const instructorAsUser = instructors.find((item: any) => {
      const instructorId = item.instructorDetails?.[0]?._id;
      const valueId = typeof value === "string" ? value : value?._id;
      return instructorId === valueId;
    }) as any;

    return instructorAsUser;
  };

  /**
   *
   */
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(onSubmit, onFormError)(e);
      }}
      method="get"
      ref={ref}
      id={FormsIds.examHours}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} sm={12} md={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Controller
              control={control}
              name={`date`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  label={Vocabulary.date}
                  value={field.value ? new Date(field.value) : null} // Convert string to Date
                  onChange={(newDate) => field.onChange(newDate)}
                  views={["month", "year", "day"]}
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Controller
              control={control}
              name={`startHour`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TimePicker
                  label={Vocabulary.startHour}
                  value={field.value ? new Date(field.value) : null} // Convert string to Date
                  onChange={(newDate) => {
                    if (newDate) {
                      field.onChange(newDate);
                      // Add 90 minutes
                      const endDate = new Date(newDate);
                      endDate.setMinutes(
                        endDate.getMinutes() +
                          GenericSettings.drivingHourInterval
                      );
                      setValue("endHour", endDate);
                    }
                  }}
                  slots={{ textField: TextField }}
                  views={["hours", "minutes"]}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                    popper: {
                      sx: {
                        "& .MuiClockPicker-root": {
                          width: "100%",
                        },
                        "& .MuiMultiSectionDigitalClock-root": {
                          width: "100%",
                        },
                        "& .MuiList-root": {
                          width: "100%",
                        },
                        "& ul": {
                          width: "100%",
                        },
                      },
                      modifiers: [
                        {
                          name: "setWidth",
                          enabled: true,
                          phase: "beforeWrite",
                          requires: ["computeStyles"],
                          fn: ({ state }) => {
                            state.styles.popper.width = `${state.rects.reference.width}px`;
                          },
                        },
                      ],
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Controller
              control={control}
              name={`endHour`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TimePicker
                  label={Vocabulary.endHour}
                  value={field.value ? new Date(field.value) : null} // Convert string to Date
                  onChange={(newDate) => field.onChange(newDate)}
                  slots={{ textField: TextField }}
                  views={["hours", "minutes"]}
                  minTime={new Date(startHour?.toString())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                    popper: {
                      sx: {
                        "& .MuiClockPicker-root": {
                          width: "100%",
                        },
                        "& .MuiMultiSectionDigitalClock-root": {
                          width: "100%",
                        },
                        "& .MuiList-root": {
                          width: "100%",
                        },
                        "& ul": {
                          width: "100%",
                        },
                      },
                      modifiers: [
                        {
                          name: "setWidth",
                          enabled: true,
                          phase: "beforeWrite",
                          requires: ["computeStyles"],
                          fn: ({ state }) => {
                            state.styles.popper.width = `${state.rects.reference.width}px`;
                          },
                        },
                      ],
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name={`instructorId`}
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                size="small"
                disableClearable
                id={`instructorId`}
                fullWidth={true}
                freeSolo={false}
                disablePortal
                noOptionsText={Vocabulary.noResultsFound}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?._id === value?._id || option?._id === value;
                }}
                getOptionLabel={(option: any) =>
                  `${option?.firstName} ${option?.lastName}`
                }
                options={[]}
                value={getValueForAutocompleteInstructors() ?? null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      rows: 4,
                      multiline: false,
                    }}
                    disabled
                    size="medium"
                    fullWidth
                    name={`instructorId`}
                    label={Vocabulary.instructor}
                    variant="outlined"
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name={`vehicleId`}
            control={control}
            defaultValue={null}
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <>
                  <Autocomplete
                    size="small"
                    disableClearable
                    id={`vehicleId`}
                    fullWidth={true}
                    className={styles.editProductOrderOffer}
                    freeSolo={false}
                    disablePortal
                    noOptionsText={Vocabulary.noResultsFound}
                    isOptionEqualToValue={(option: any, value: any) => {
                      if (typeof value === "string") {
                        return option._id === value;
                      } else {
                        return option?._id === value?._id;
                      }
                    }}
                    getOptionLabel={(option: any) =>
                      getVehicleLabel(option, vehicles)
                    }
                    options={vehicles || []}
                    value={field.value}
                    onChange={(
                      e: any,
                      product: string,
                      reason: AutocompleteChangeReason,
                      details?: any
                    ) => {
                      field.onChange(details.option._id);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          rows: 4,
                          multiline: false,
                          inputComponent: "textarea",
                        }}
                        fullWidth={true}
                        name={`vehicleId`}
                        label={Vocabulary.vehicle}
                        variant="outlined"
                        size="medium"
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </>
              );
            }}
          />
        </Grid>
      </Grid>
    </form>
  );
}
